import Big from 'big.js';
import { forwardRef, useCallback, type ForwardedRef } from 'react';
import { asyncScheduler, type Observable } from 'rxjs';
import { distinctUntilChanged, map, throttleTime } from 'rxjs/operators';

import { defineMessages } from 'react-intl';
import { useObservable, useObservableValue } from '../../hooks/useObservable';
import type { MarketDataSnapshot } from '../../types/MarketDataSnapshot';
import type { Security } from '../../types/Security';
import { IconButton } from '../Button';
import { HStack } from '../Core';
import { IconName } from '../Icons';
import { FormattedMessage } from '../Intl';
import { Spinner } from '../Spinner';
import { Row, type ClickRowCallback } from './Row';
import { SpreadRow } from './SpreadRow';
import {
  BidLabel,
  Footer,
  Header,
  Increment,
  IncrementWrapper,
  Labels,
  Ladder,
  LeftLabel,
  OfferLabel,
  PriceLabel,
  PriceLadderWrapper,
  QTY_LIMIT,
  RightLabel,
} from './styles';
import { StepType } from './types';
import { formatMarketData, type StepData } from './utils';

export { calculatePriceLadderDepth, getPriceLadderSnapshotLevels, getPriceLadderSteps } from './utils';

const DEFAULT_WIDTH = 320;

const DEFAULT_BUCKETS = [
  '0.0001',
  '0.0002',
  '0.0005',
  '0.001',
  '0.002',
  '0.005',
  '0.01',
  '0.02',
  '0.05',
  '0.1',
  '0.2',
  '0.5',
  '1',
  '2',
  '5',
  '10',
  '20',
  '50',
  '100',
  '200',
  '500',
  '1000',
  '2000',
  '5000',
  '10000',
  '20000',
  '50000',
  '100000',
  '200000',
  '500000',
  '1000000',
  '2000000',
  '5000000',
  '10000000',
  '20000000',
  '50000000',
  '100000000',
  '200000000',
  '500000000',
  '1000000000',
  '2000000000',
  '5000000000',
];

const messages = defineMessages({
  qty: {
    defaultMessage: 'Qty',
    id: 'PriceLadder.qty',
  },
  price: {
    defaultMessage: 'Price',
    id: 'PriceLadder.price',
  },
  total: {
    defaultMessage: 'Total',
    id: 'PriceLadder.total',
  },
  bids: {
    defaultMessage: 'Bids',
    id: 'PriceLadder.bids',
  },
  offers: {
    defaultMessage: 'Offers',
    id: 'PriceLadder.offers',
  },
});

export const PriceLadder = forwardRef<HTMLDivElement, PriceLadderProps>(
  (
    {
      marketDataObservable,
      security,
      width = DEFAULT_WIDTH,
      increment,
      steps,
      onIncrementChange,
      onClickRow,
    }: PriceLadderProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const stepsObservable = useObservable<StepData>(
      () =>
        marketDataObservable.pipe(
          throttleTime(1000, asyncScheduler, { leading: true, trailing: true }),
          map(data => formatMarketData(data, steps, increment))
        ),
      [marketDataObservable, steps, increment]
    );
    const { bids, offers, maxSize, topOffer, topBid, spread, spreadBps } =
      useObservableValue(() => stepsObservable, [stepsObservable]) ?? {};
    const bucketObservable = useObservable<number>(
      () =>
        stepsObservable.pipe(
          map(({ maxSize }) => {
            const buckets = DEFAULT_BUCKETS;
            // Find the first bucket larger than the maxSize
            return parseFloat(buckets.find(bucket => maxSize < parseFloat(bucket)) ?? DEFAULT_BUCKETS.at(-1));
          }),
          distinctUntilChanged(),
          // Send initial (leading) value, then wait 60s before sending the last seen (trailing) value
          throttleTime(60000, asyncScheduler, { leading: true, trailing: true })
        ),
      [stepsObservable]
    );
    const bucket = useObservableValue<number>(() => bucketObservable, [bucketObservable]) ?? 1;
    const updateIncrement = useCallback(
      (factor: number) => {
        const newIncrement = Big(increment ?? '0').times(factor);
        const minIncrement = security?.MinPriceIncrement ?? '0';
        onIncrementChange(newIncrement.lt(minIncrement) ? minIncrement : newIncrement.toFixed());
      },
      [security, increment, onIncrementChange]
    );

    const decreaseIncrement = useCallback(() => updateIncrement(0.1), [updateIncrement]);
    const increaseIncrement = useCallback(() => updateIncrement(10), [updateIncrement]);

    return (
      <PriceLadderWrapper ref={ref}>
        <Labels>
          <BidLabel>
            <FormattedMessage {...messages.bids} />
          </BidLabel>
          <IncrementWrapper>
            <IconButton ghost icon={IconName.MinusBox} onClick={decreaseIncrement} color="colorTextDefault" />
            <Increment>{increment}</Increment>
            <IconButton ghost icon={IconName.PlusBox} onClick={increaseIncrement} color="colorTextDefault" />
          </IncrementWrapper>
          <OfferLabel>
            <FormattedMessage {...messages.offers} />
          </OfferLabel>
        </Labels>
        <Header>
          <LeftLabel>
            <FormattedMessage {...messages.total} />
          </LeftLabel>
          {width > QTY_LIMIT && (
            <LeftLabel>
              <FormattedMessage {...messages.qty} />
            </LeftLabel>
          )}
          <PriceLabel>
            <FormattedMessage {...messages.price} />
          </PriceLabel>
          {width > QTY_LIMIT && (
            <RightLabel>
              <FormattedMessage {...messages.qty} />
            </RightLabel>
          )}
          <RightLabel>
            <FormattedMessage {...messages.total} />
          </RightLabel>
        </Header>
        <Ladder>
          {security == null ? (
            <HStack>No symbol selected</HStack>
          ) : bids == null || offers == null || maxSize == null ? (
            <Spinner />
          ) : (
            <>
              {offers.map((offer, i) => (
                <Row
                  value={offer}
                  increment={increment}
                  maxSize={bucket}
                  type={StepType.Offer}
                  onClickRow={onClickRow}
                  width={width}
                  key={i}
                />
              ))}
              <SpreadRow
                spread={spread}
                security={security}
                spreadBps={spreadBps}
                topBid={topBid}
                topOffer={topOffer}
                symbol={security.Symbol}
                onClickRow={onClickRow}
              />
              {bids.map((bid, i) => (
                <Row
                  value={bid}
                  increment={increment}
                  maxSize={bucket}
                  type={StepType.Bid}
                  onClickRow={onClickRow}
                  width={width}
                  key={i}
                />
              ))}
            </>
          )}
        </Ladder>
        <Footer>
          <LeftLabel>
            <FormattedMessage {...messages.total} />
          </LeftLabel>
          {width > QTY_LIMIT && (
            <LeftLabel>
              <FormattedMessage {...messages.qty} />
            </LeftLabel>
          )}
          <PriceLabel>
            <FormattedMessage {...messages.price} />
          </PriceLabel>
          {width > QTY_LIMIT && (
            <RightLabel>
              <FormattedMessage {...messages.qty} />
            </RightLabel>
          )}
          <RightLabel>
            <FormattedMessage {...messages.total} />
          </RightLabel>
        </Footer>
        <Labels>
          <BidLabel>
            <FormattedMessage {...messages.bids} />
          </BidLabel>
          <OfferLabel>
            <FormattedMessage {...messages.offers} />
          </OfferLabel>
        </Labels>
      </PriceLadderWrapper>
    );
  }
);

export type PriceLadderProps = {
  marketDataObservable: Observable<MarketDataSnapshot>;
  security?: Security;
  increment: string;
  steps: number;
  width?: number;
  onIncrementChange: (increment: string) => void;
  onClickRow: ClickRowCallback;
};
